<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Spinner,
		Alert,
		Checkbox,
		Fileupload,
		StepIndicator,
		Label
	} from 'flowbite-svelte';
	import Papa from 'papaparse';
	import PageHeader from '../../components/PageHeader.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import { Panel, RoutedButton } from '../../components';
	import { listApplications, importUsers, previewImport, isValidEmail } from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import type { Application } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	// ── Step state ────────────────────────────────────────────────

	/** Current wizard step (1-indexed). */
	let currentStep = $state(1);
	/** Step labels for the indicator. */
	const steps = [t2('Upload'), t2('Map Columns'), t2('Applications'), t2('Review'), t2('Confirm')];

	// ── Step 1: Upload ────────────────────────────────────────────

	/** Raw parsed rows (array of string arrays). */
	let rawHeaders: string[] = $state([]);
	let rawRows: string[][] = $state([]);
	/** File parsing error. */
	let parseError = $state('');

	/** Handle file upload. */
	function handleFile(e: Event): void {
		parseError = '';
		const input = e.target as HTMLInputElement;
		const file = input?.files?.[0];
		/** Bail out if no file was selected. */
		if (!file) return;

		Papa.parse(file, {
			skipEmptyLines: true,
			complete(results: Papa.ParseResult<string[]>) {
				if (results.errors.length > 0) {
					parseError = results.errors.map((e) => e.message).join('; ');
					return;
				}
				const data = results.data as string[][];
				/** Require at least one data row beyond the header. */
				if (data.length < 2) {
					parseError = 'File must contain at least a header row and one data row.';
					return;
				}
				rawHeaders = data[0];
				rawRows = data.slice(1);
			},
			error(err: Error) {
				parseError = err.message;
			}
		});
	}

	// ── Step 2: Column mapping ────────────────────────────────────

	/** Selected column index for each field ("-1" = unmapped). */
	let emailCol = $state('-1');
	let firstNameCol = $state('-1');
	let lastNameCol = $state('-1');

	/** Auto-detect column mappings from header names. */
	function autoDetect(): void {
		/** Normalise header for fuzzy matching. */
		const fnNorm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
		rawHeaders.forEach((h, i) => {
			const n = fnNorm(h);
			if (n === 'email' || n === 'emailaddress' || n === 'mail') emailCol = String(i);
			else if (n === 'firstname' || n === 'first' || n === 'givenname') firstNameCol = String(i);
			else if (n === 'lastname' || n === 'last' || n === 'surname' || n === 'familyname') lastNameCol = String(i);
		});
	}

	/** Build the mapped row preview (first 5). */
	let mappedPreview = $derived(rawRows.slice(0, 5).map((row) => ({
		email: emailCol !== '-1' ? (row[Number(emailCol)] ?? '') : '',
		firstName: firstNameCol !== '-1' ? (row[Number(firstNameCol)] ?? '') : '',
		lastName: lastNameCol !== '-1' ? (row[Number(lastNameCol)] ?? '') : ''
	})));

	/** Whether the mapping is valid: email column is required. */
	let mappingValid = $derived(emailCol !== '-1');

	// ── Step 3: Applications ──────────────────────────────────────

	/** All available applications. */
	let applications: Application[] = $state([]);
	/** Selected application IDs. */
	let selectedAppIds: number[] = $state([]);

	/** Load applications on mount. */
	onMount(async () => {
		try {
			applications = await listApplications();
		} catch {
			/* non-critical */
		}
	});

	/** Toggle an application selection. */
	function toggleApp(id: number): void {
		if (selectedAppIds.includes(id)) {
			selectedAppIds = selectedAppIds.filter((a) => a !== id);
		} else {
			selectedAppIds = [...selectedAppIds, id];
		}
	}

	// ── Step 4: Review ────────────────────────────────────────────

	/** A row that failed validation. */
	type InvalidRow = { row: number; email: string; reason: string };

	/** New rows that will be created on import. */
	let newRows: Array<{ email: string; firstName?: string; lastName?: string }> = $state([]);
	/** Rows whose email already exists in the database. */
	let existingRows: Array<{ email: string; firstName?: string; lastName?: string }> = $state([]);
	/** Rows that failed client-side validation (bad format, duplicate in file, etc.). */
	let invalidRows: InvalidRow[] = $state([]);
	/** Whether the review is loading (server check in progress). */
	let reviewing = $state(false);

	/** Combined valid rows (format-valid, unique in file). Split into new/existing after server check. */
	let validRows: Array<{ email: string; firstName?: string; lastName?: string }> = [];

	/**
	 * Run client-side validation on all mapped rows, then call the server
	 * to check which valid emails already exist in the database.
	 */
	async function validateRows(): Promise<void> {
		validRows = [];
		existingRows = [];
		newRows = [];
		invalidRows = [];
		const seen = new Set<string>(); // eslint-disable-line svelte/prefer-svelte-reactivity -- local variable, not reactive state

		rawRows.forEach((row, i) => {
			const email = (emailCol !== '-1' ? (row[Number(emailCol)] ?? '') : '').trim().toLowerCase();
			const firstName = (firstNameCol !== '-1' ? (row[Number(firstNameCol)] ?? '') : '').trim() || undefined;
			const lastName = (lastNameCol !== '-1' ? (row[Number(lastNameCol)] ?? '') : '').trim() || undefined;
			const rowNum = i + 2; // +2 for 1-indexed + header row

			/** Classify each row as invalid (missing/bad email/duplicate) or valid. */
			if (!email) {
				invalidRows.push({ row: rowNum, email: '(empty)', reason: 'Missing email' });
			} else if (!isValidEmail(email)) {
				invalidRows.push({ row: rowNum, email, reason: 'Invalid email format' });
			} else if (seen.has(email)) {
				invalidRows.push({ row: rowNum, email, reason: 'Duplicate in file' });
			} else {
				seen.add(email);
				validRows.push({ email, firstName, lastName });
			}
		});

		/* Ask the server which valid emails already exist. */
		if (validRows.length > 0) {
			reviewing = true;
			try {
				const { existing } = await previewImport(validRows.map((r) => r.email));
				const existingSet = new Set(existing.map((e: string) => e.toLowerCase()));
				newRows = validRows.filter((r) => !existingSet.has(r.email));
				existingRows = validRows.filter((r) => existingSet.has(r.email));
			} catch {
				/* If preview fails, treat all valid rows as new (server import will still dedup). */
				newRows = validRows;
				existingRows = [];
			} finally {
				reviewing = false;
			}
		}
	}

	// ── Step 5: Confirm & Submit ──────────────────────────────────

	/** Whether the import is in progress. */
	let importing = $state(false);
	/** Import result. */
	let importResult: { created: number; skipped: number; errors: string[] } | null = $state(null);

	/** Execute the import using only new rows (existing are excluded). */
	async function doImport(): Promise<void> {
		importing = true;
		/** Attempt the server-side import and report the outcome. */
		try {
			importResult = await importUsers(newRows, selectedAppIds);
			toastSuccess(t2(`Imported ${importResult.created} users (${importResult.skipped} skipped).`));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Import failed'));
		} finally {
			importing = false;
		}
	}

	// ── Navigation ────────────────────────────────────────────────

	/** Move to the next step, auto-detecting columns on step 1→2 and validating on step 3→4. */
	async function nextStep(): Promise<void> {
		if (currentStep === 1) autoDetect();
		if (currentStep === 3) await validateRows();
		currentStep = Math.min(currentStep + 1, 5);
	}

	/**
	 *
	 */
	function prevStep(): void {
		currentStep = Math.max(currentStep - 1, 1);
	}

	/** Column select options (for step 2). */
	let columnOptions = $derived([
		{ value: '-1', name: t2('— Not mapped —'), icon: 'fa-solid fa-ban' },
		...rawHeaders.map((h, i) => ({ value: String(i), name: h || `Column ${i + 1}`, icon: 'fa-solid fa-table-columns' }))
	]);

</script>

<PageHeader title={$t("Import Users")} breadcrumbs={[{ label: $t('Users'), href: '/Users' }, { label: $t('Import') }]} />

<div class="mx-auto mt-6 max-w-3xl space-y-6">
	<!-- Step indicator -->
	<StepIndicator {currentStep} {steps} />

	<!-- ───── Step 1: Upload ───── -->
	{#if currentStep === 1}
		<Panel>
			<h3 class="mb-3 text-lg font-semibold dark:text-white">{$t("Upload CSV File")}</h3>
			<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
				{$t("Upload a CSV file containing the users you want to import. The first row should contain column headers (e.g. Email, First Name, Last Name).")}
			</p>

			<Fileupload accept=".csv,text/csv" onchange={handleFile} />

			{#if parseError}
				<Alert color="red" class="mt-4">{parseError}</Alert>
			{/if}

			{#if rawRows.length > 0}
				<p class="mt-4 text-sm text-green-600 dark:text-green-400">
					<i class="fa-solid fa-check mr-1"></i>
					{$t("Parsed {{rows}} rows with {{cols}} columns.", { rows: rawRows.length, cols: rawHeaders.length })}
				</p>
			{/if}

			<div class="mt-6 flex justify-between">
				<RoutedButton color="alternative" href="/Users">{$t("Cancel")}</RoutedButton>
				<Button color="primary" disabled={rawRows.length === 0} onclick={nextStep}>
					{$t("Next")} <i class="fa-solid fa-arrow-right ml-2"></i>
				</Button>
			</div>
		</Panel>
	{/if}

	<!-- ───── Step 2: Map Columns ───── -->
	{#if currentStep === 2}
		<Panel>
			<h3 class="mb-3 text-lg font-semibold dark:text-white">{$t("Map Columns")}</h3>
			<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
				{$t("Assign CSV columns to user fields.")} <strong>{$t("Email")}</strong> {$t("is required.")}
			</p>

			<div class="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
				<div>
					<Label class="mb-1">{$t("Email")} <span class="text-red-500">*</span></Label>
					<AbstractDropdown items={columnOptions} bind:value={emailCol} />
				</div>
				<div>
					<Label class="mb-1">{$t("First Name")}</Label>
					<AbstractDropdown items={columnOptions} bind:value={firstNameCol} />
				</div>
				<div>
					<Label class="mb-1">{$t("Last Name")}</Label>
					<AbstractDropdown items={columnOptions} bind:value={lastNameCol} />
				</div>
			</div>

			{#if mappedPreview.length > 0}
				<h4 class="mb-2 text-sm font-medium dark:text-white">{$t("Preview (first 5 rows)")}</h4>
				<Table divClass="widget !p-1 relative" class="dataTable">
					<TableHead>
						<TableHeadCell>{$t("Email")}</TableHeadCell>
						<TableHeadCell>{$t("First Name")}</TableHeadCell>
						<TableHeadCell>{$t("Last Name")}</TableHeadCell>
					</TableHead>
					<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
						{#each mappedPreview as row, i (i)}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell>{row.email || '—'}</TableBodyCell>
								<TableBodyCell>{row.firstName || '—'}</TableBodyCell>
								<TableBodyCell>{row.lastName || '—'}</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}

			<div class="mt-6 flex justify-between">
				<Button color="alternative" onclick={prevStep}>
					<i class="fa-solid fa-arrow-left mr-2"></i> {$t("Back")}
				</Button>
				<Button color="primary" disabled={!mappingValid} onclick={nextStep}>
					{$t("Next")} <i class="fa-solid fa-arrow-right ml-2"></i>
				</Button>
			</div>
		</Panel>
	{/if}

	<!-- ───── Step 3: Applications ───── -->
	{#if currentStep === 3}
		<Panel>
			<h3 class="mb-3 text-lg font-semibold dark:text-white">{$t("Register to Applications")}</h3>
			<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
				{$t("Optionally register imported users to one or more applications. This step is optional — you can skip it to import users without application registrations.")}
			</p>

			{#if applications.length === 0}
				<p class="text-sm italic text-gray-400">{$t("No applications found.")}</p>
			{:else}
				<div class="space-y-2">
					{#each applications as app (app.id)}
						<div class="flex items-center gap-3 rounded p-2 hover:bg-gray-50 dark:hover:bg-gray-700">
							<Checkbox checked={selectedAppIds.includes(app.id)} onchange={() => toggleApp(app.id)} />
							<div>
								<span class="font-medium dark:text-white">{app.applicationName}</span>
								{#if app.description}
									<span class="ml-2 text-xs text-gray-400">{app.description}</span>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			{/if}

			<div class="mt-6 flex justify-between">
				<Button color="alternative" onclick={prevStep}>
					<i class="fa-solid fa-arrow-left mr-2"></i> {$t("Back")}
				</Button>
				<Button color="primary" onclick={nextStep}>
					{$t("Next")} <i class="fa-solid fa-arrow-right ml-2"></i>
				</Button>
			</div>
		</Panel>
	{/if}

	<!-- ───── Step 4: Review ───── -->
	{#if currentStep === 4}
		<Panel>
			<h3 class="mb-3 text-lg font-semibold dark:text-white">{$t("Review Data")}</h3>
			<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
				{$t("Review the validation results below before importing.")}
			</p>

			{#if reviewing}
				<div class="flex items-center justify-center py-8">
					<Spinner size="6" class="mr-3" />
					<span class="text-gray-500 dark:text-gray-400">{$t("Checking for existing users…")}</span>
				</div>
			{:else}
				<!-- Summary cards -->
				<div class="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
					<div
						class="rounded-lg border border-green-200 bg-green-50 p-4 text-center dark:border-green-800 dark:bg-green-900/20"
					>
						<p class="text-2xl font-bold text-green-600 dark:text-green-400">{newRows.length}</p>
						<p class="text-sm text-green-700 dark:text-green-300">{$t("New users")}</p>
					</div>
					<div
						class="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center dark:border-yellow-800 dark:bg-yellow-900/20"
					>
						<p class="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{existingRows.length}</p>
						<p class="text-sm text-yellow-700 dark:text-yellow-300">{$t("Already exist")}</p>
					</div>
					<div
						class="rounded-lg border border-red-200 bg-red-50 p-4 text-center dark:border-red-800 dark:bg-red-900/20"
					>
						<p class="text-2xl font-bold text-red-600 dark:text-red-400">{invalidRows.length}</p>
						<p class="text-sm text-red-700 dark:text-red-300">{$t("Invalid")}</p>
					</div>
					<div
						class="rounded-lg border border-gray-200 bg-depth p-4 text-center dark:border-gray-700"
					>
						<p class="text-2xl font-bold text-gray-600 dark:text-gray-300">{rawRows.length}</p>
						<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Total rows")}</p>
					</div>
				</div>

				{#if existingRows.length > 0}
					<h4 class="mb-2 text-sm font-medium dark:text-white">
						<i class="fa-solid fa-user-check mr-1 text-yellow-500"></i>
						{$t("Already exist — will be skipped ({{count}})", { count: existingRows.length })}
					</h4>
					<div class="mb-4 max-h-48 overflow-y-auto">
						<Table divClass="widget !p-1 relative" class="dataTable">
							<TableHead>
								<TableHeadCell>{$t("Email")}</TableHeadCell>
								<TableHeadCell>{$t("First Name")}</TableHeadCell>
								<TableHeadCell>{$t("Last Name")}</TableHeadCell>
							</TableHead>
							<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
								{#each existingRows.slice(0, 50) as row (row.email)}
									<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
										<TableBodyCell class="font-mono text-xs">{row.email}</TableBodyCell>
										<TableBodyCell>{row.firstName ?? ''}</TableBodyCell>
										<TableBodyCell>{row.lastName ?? ''}</TableBodyCell>
									</TableBodyRow>
								{/each}
								{#if existingRows.length > 50}
									<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
										<TableBodyCell colspan={3} class="text-center text-gray-400">
											{$t("…and {{count}} more", { count: existingRows.length - 50 })}
										</TableBodyCell>
									</TableBodyRow>
								{/if}
							</TableBody>
						</Table>
					</div>
				{/if}

				{#if invalidRows.length > 0}
					<h4 class="mb-2 text-sm font-medium dark:text-white">
						<i class="fa-solid fa-triangle-exclamation mr-1 text-red-500"></i>
						{$t("Invalid rows ({{count}})", { count: invalidRows.length })}
					</h4>
					<div class="mb-4 max-h-48 overflow-y-auto">
						<Table divClass="widget !p-1 relative" class="dataTable">
							<TableHead>
								<TableHeadCell>{$t("Row")}</TableHeadCell>
								<TableHeadCell>{$t("Email")}</TableHeadCell>
								<TableHeadCell>{$t("Reason")}</TableHeadCell>
							</TableHead>
							<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
								{#each invalidRows.slice(0, 50) as inv (inv.row)}
									<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
										<TableBodyCell>{inv.row}</TableBodyCell>
										<TableBodyCell class="font-mono text-xs">{inv.email}</TableBodyCell>
										<TableBodyCell>
											<Badge color="red">{inv.reason}</Badge>
										</TableBodyCell>
									</TableBodyRow>
								{/each}
								{#if invalidRows.length > 50}
									<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
										<TableBodyCell colspan={3} class="text-center text-gray-400">
											{$t("…and {{count}} more", { count: invalidRows.length - 50 })}
										</TableBodyCell>
									</TableBodyRow>
								{/if}
							</TableBody>
						</Table>
					</div>
				{/if}

				{#if newRows.length === 0}
					<Alert color="red" class="mb-4">
						{$t("No new users to import. All valid email addresses already exist in the system.")}
					</Alert>
				{:else}
					<Alert color="green" class="mb-4">
						<strong>{newRows.length}</strong> {$t("new user(s) will be imported.")}
						{#if existingRows.length > 0}
							<strong>{existingRows.length}</strong> {$t("already exist and will be skipped.")}
						{/if}
					</Alert>
				{/if}
			{/if}

			<div class="mt-6 flex justify-between">
				<Button color="alternative" onclick={prevStep}>
					<i class="fa-solid fa-arrow-left mr-2"></i> {$t("Back")}
				</Button>
				<Button color="primary" disabled={reviewing || newRows.length === 0} onclick={nextStep}>
					{$t("Next")} <i class="fa-solid fa-arrow-right ml-2"></i>
				</Button>
			</div>
		</Panel>
	{/if}

	<!-- ───── Step 5: Confirm ───── -->
	{#if currentStep === 5}
		<Panel>
			<h3 class="mb-3 text-lg font-semibold dark:text-white">{$t("Confirm Import")}</h3>

			{#if !importResult}
				<div class="mb-6 space-y-3 text-sm dark:text-gray-300">
					<p><strong>{$t("New users to import:")}</strong> {newRows.length}</p>
					{#if existingRows.length > 0}
						<p class="text-yellow-600 dark:text-yellow-400">
							<strong>{$t("Already exist (skipped):")}</strong>
							{existingRows.length}
						</p>
					{/if}
					{#if invalidRows.length > 0}
						<p class="text-red-600 dark:text-red-400">
							<strong>{$t("Invalid (skipped):")}</strong>
							{invalidRows.length}
						</p>
					{/if}
					<p>
						<strong>{$t("Applications:")}</strong>
						{selectedAppIds.length > 0
							? applications
									.filter((a) => selectedAppIds.includes(a.id))
									.map((a) => a.applicationName)
									.join(', ')
							: $t('None')}
					</p>
					<p class="text-xs text-gray-400">
						{$t("Imported users will be created as dormant accounts (authProvider = 'imported'). They can receive newsletters and log in via email OTP without needing to register separately.")}
					</p>
				</div>

				<div class="mt-6 flex justify-between">
					<Button color="alternative" onclick={prevStep}>
						<i class="fa-solid fa-arrow-left mr-2"></i> {$t("Back")}
					</Button>
					<Button color="primary" disabled={importing || newRows.length === 0} onclick={doImport}>
						{#if importing}
							<Spinner size="4" class="mr-2" /> {$t("Importing…")}
						{:else}
							<i class="fa-solid fa-file-import mr-2"></i> {$t("Import {{count}} User(s)", { count: newRows.length })}
						{/if}
					</Button>
				</div>
			{:else}
				<!-- Result summary -->
				<div class="mb-6 space-y-3">
					<Alert color="green">
						<strong>{importResult.created}</strong> {$t("users imported successfully.")} <strong>{importResult.skipped}</strong> {$t("skipped (already exist).")}
					</Alert>

					{#if importResult.errors.length > 0}
						<Alert color="yellow">
							<strong>{importResult.errors.length}</strong> {$t("errors:")}
							<ul class="ml-4 mt-1 list-disc text-sm">
								{#each importResult.errors.slice(0, 10) as err, i (i)}
									<li>{err}</li>
								{/each}
								{#if importResult.errors.length > 10}
									<li class="text-gray-400">{$t("…and {{count}} more", { count: importResult.errors.length - 10 })}</li>
								{/if}
							</ul>
						</Alert>
					{/if}
				</div>

				<div class="flex justify-end">
					<RoutedButton color="primary" href="/Users">
						<i class="fa-solid fa-arrow-left mr-2"></i> {$t("Back to Users")}
					</RoutedButton>
				</div>
			{/if}
		</Panel>
	{/if}
</div>
